import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { money } from "../api";

const FALLBACK = [
  { id: "f1", name: "Habesha dress", image: "/hero/dress.jpg", price: 89, slug: "shop" },
  { id: "f2", name: "Fresh injera", image: "/hero/injera.jpg", price: 9, slug: "shop" },
  { id: "f3", name: "Jebena coffee", image: "/hero/jebena.jpg", price: 25, slug: "shop" },
  { id: "f4", name: "Roasted kolos", image: "/hero/kolos.jpg", price: 5.5, slug: "shop" },
  { id: "f5", name: "Teff grain", image: "/hero/injera.jpg", price: 12, slug: "shop" },
];

function normalizeItems(products, slides) {
  if (products?.length) {
    return products.map((p) => ({
      id: p.id,
      name: p.name,
      image: p.image,
      price: p.price,
      slug: p.slug,
    }));
  }
  if (slides?.length) {
    return slides
      .filter((s) => s.src || s.image)
      .map((s, i) => ({
        id: `slide-${i}`,
        name: s.label || s.title || "Featured",
        image: s.src || s.image,
        price: null,
        slug: null,
      }));
  }
  return FALLBACK;
}

/** Circular offset from active index into [-floor(n/2), ...] */
function offsetFrom(i, active, n) {
  let d = i - active;
  if (d > n / 2) d -= n;
  if (d < -n / 2) d += n;
  return d;
}

export default function HeroProductCarousel({ products = [], slides = [], intervalMs = 3200 }) {
  const items = useMemo(() => normalizeItems(products, slides), [products, slides]);
  const n = items.length;
  const [active, setActive] = useState(0);
  const [step, setStep] = useState(138);

  useEffect(() => {
    // card width + gap so slides don't overlap
    const sync = () => setStep(window.innerWidth < 700 ? 170 : window.innerWidth < 1000 ? 230 : 290);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    setActive(0);
  }, [n]);

  useEffect(() => {
    if (n < 2) return undefined;
    const id = window.setInterval(() => setActive((a) => (a + 1) % n), intervalMs);
    return () => window.clearInterval(id);
  }, [n, intervalMs]);

  if (!n) return null;

  return (
    <div className="hero-coverflow" aria-roledescription="carousel" aria-label="Featured products">
      <div className="hero-coverflow-stage">
        {items.map((item, i) => {
          const offset = offsetFrom(i, active, n);
          const visible = Math.abs(offset) <= 2;
          const abs = Math.abs(offset);
          // Center only 10% larger; neighbors stay normal size
          const scale = offset === 0 ? 1.1 : 1;
          const x = offset * step;
          const z = 20 - abs;
          const opacity = visible ? (offset === 0 ? 1 : abs === 1 ? 0.92 : 0.7) : 0;

          const card = (
            <article
              className={`hero-cover-card ${offset === 0 ? "is-center" : ""}`}
              style={{
                transform: `translate(-50%, -50%) translateX(${x}px) scale(${scale})`,
                zIndex: z,
                opacity,
                pointerEvents: visible ? "auto" : "none",
              }}
              aria-hidden={!visible}
            >
              <div className="hero-cover-media">
                <img src={item.image} alt={item.name} loading={abs <= 1 ? "eager" : "lazy"} />
                <div className="hero-cover-meta">
                  <h3>{item.name}</h3>
                  {item.price != null && <span>{money(item.price)}</span>}
                </div>
              </div>
            </article>
          );

          if (item.slug && item.slug !== "shop") {
            return (
              <Link key={item.id} to={`/product/${item.slug}`} className="hero-cover-link" tabIndex={offset === 0 ? 0 : -1}>
                {card}
              </Link>
            );
          }
          if (item.slug === "shop") {
            return (
              <Link key={item.id} to="/shop" className="hero-cover-link" tabIndex={offset === 0 ? 0 : -1}>
                {card}
              </Link>
            );
          }
          return (
            <div key={item.id} className="hero-cover-link">
              {card}
            </div>
          );
        })}
      </div>

      <div className="hero-coverflow-controls">
        <button type="button" className="hero-cover-nav" aria-label="Previous" onClick={() => setActive((a) => (a - 1 + n) % n)}>
          ‹
        </button>
        <div className="hero-slideshow-dots" role="tablist">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={item.name}
              className={i === active ? "is-active" : ""}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
        <button type="button" className="hero-cover-nav" aria-label="Next" onClick={() => setActive((a) => (a + 1) % n)}>
          ›
        </button>
      </div>
    </div>
  );
}
